import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@customer/store/hooks';
import { skipToken } from '@reduxjs/toolkit/query';
import { ConnectivitySnackbar } from '@dmc/ui';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import Page from '@customer/templates/page/Page';
import { useGetInvestigationQuery } from '@customer/store/apis/investigationsApi';
import styles from './Investigation.module.scss';
import TabbedContent from './components/tabbed-content/TabbedContent';
import InvestigationMap from './components/map/InvestigationMap';
import InvestigationAssets from './components/assets/InvestigationAssets';
import { useSiteSwitchWatcher } from '@customer/hooks/useSiteSwitchWatcher';
import SegmentedGroup, {
  SegmentedButtonProps,
} from '@cas/components/inputs/segmented-group/SegmentedGroup';
import { split, table } from '@cas/components/icons/icons';
import Skeleton from 'react-loading-skeleton';
import {
  InvestigationSegment,
  setInvestigationView,
} from '@customer/store/slices/investigationGuiSlice';
import PlaybackControls from './components/playback-controls/PlaybackControls';
import { InvestigationHeader } from './components/investigation-header/InvestigationHeader';
import { ModelName } from '@customer/store/types';

const segmentedButtons: SegmentedButtonProps<InvestigationSegment>[] = [
  { value: InvestigationSegment.Map, icon: 'map' },
  { value: InvestigationSegment.Split, icon: split },
  { value: InvestigationSegment.Tables, icon: table },
];

export function Investigation() {
  const { investigationId } = useParams();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  useSiteSwitchWatcher('/investigations');

  const siteId = useAppSelector(state => state.auth.siteId);

  const isSkipQuery = typeof investigationId === 'undefined' || typeof siteId === 'undefined';

  const { currentData: investigation, error: fetchError } = useGetInvestigationQuery(
    isSkipQuery ? skipToken : { siteId, investigationId },
    { refetchOnMountOrArgChange: 60 },
  );

  const hasLoaded = typeof investigation !== 'undefined';

  const view = useAppSelector(state => state.investigationGui.view);

  const segmentedViewChanged = useCallback(
    (value: InvestigationSegment) => {
      if (hasLoaded) {
        dispatch(setInvestigationView(value));
      }
    },
    [dispatch, hasLoaded],
  );

  return (
    <>
      <Page fullWidth header={<InvestigationHeader investigation={investigation} />}>
        <div className={styles.container}>
          <InvestigationAssets assets={investigation?.assets} />

          <div className={styles.controls}>
            <PlaybackControls />
            {hasLoaded ? (
              <SegmentedGroup
                items={segmentedButtons}
                initialValue={view}
                onValueChange={segmentedViewChanged}
              />
            ) : (
              <Skeleton width={114} height={36} />
            )}
          </div>

          <div
            className={styles.content}
            style={
              {
                '--cas-investigation-cols': view === InvestigationSegment.Split ? 2 : 1,
              } as React.CSSProperties
            }
          >
            {typeof investigation !== 'undefined' ? (
              <>
                {(view === InvestigationSegment.Map || view === InvestigationSegment.Split) && (
                  <InvestigationMap key="map" />
                )}
                {(view === InvestigationSegment.Tables || view === InvestigationSegment.Split) && (
                  <TabbedContent key="tables" investigationType={investigation.it} />
                )}
              </>
            ) : (
              <Skeleton height="100%" />
            )}
          </div>
        </div>
      </Page>
      <ConnectivitySnackbar
        error={fetchError}
        message={t('Model.FetchError', { modelName: ModelName.Investigation })}
        title={t('Error')}
      />
    </>
  );
}

export default Investigation;
